import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  LButton,
  LNotificationGroup,
  LNotification,
  LFade,
} from "@loffa/gpp-modules";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck, faCodeCompare, faXmark } from "@fortawesome/free-solid-svg-icons";
import styles from "./GabSyncManagement.module.css";
// AuthContext lives at src/context; this file is four levels deep under src/.
import { AuthContext } from "../../../../context/AuthContext";
import {
  applyRun,
  cancelRun,
  fetchCompareDefaults,
  fetchRun,
  fetchRunsForProduct,
  startCompare,
  submitResolutions,
  validateCustom,
} from "./gabSyncApi";
import {
  CustomFieldError,
  GabCompareDefaults,
  GabRowResolutionType,
  GabSyncRunDetail,
  GabSyncRunSummary,
  RowDiff,
  RowResolution,
  SyncSession,
  rowKey,
} from "./types";
import {
  BulkTarget,
  CustomPayloadMap,
  ResolutionMap,
  applyResolution,
  bulkResolve,
  initialResolutions,
  isResolvable,
  parentLinks,
  summarize,
  toResolutionList,
} from "./resolutionModel";
import { SyncStartPanel } from "./SyncStartPanel";
import { SyncReview } from "./SyncReview";
import { ApplyPanel } from "./ApplyPanel";
import { RunHistory } from "./RunHistory";

type Mode = "compare" | "history";
type Step = "select" | "review" | "apply" | "result";

interface Notice {
  style: "success" | "error" | "info";
  message: string;
}

// Seed the resolution map from a run: use previously-saved resolutions when resuming, otherwise the
// New=>TakeSource / Changed=>Keep defaults, filling any gaps with defaults.
function seedResolutions(session: SyncSession, saved: RowResolution[]): ResolutionMap {
  const defaults = initialResolutions(session);
  if (!saved || saved.length === 0) return defaults;
  const map: ResolutionMap = { ...defaults };
  for (const r of saved) {
    map[rowKey(r.tableCode, r.keyValue)] = r.resolution;
  }
  return map;
}

// Restore previously-saved custom payloads when resuming a run.
function seedCustomPayloads(saved: RowResolution[]): CustomPayloadMap {
  const map: CustomPayloadMap = {};
  for (const r of saved ?? []) {
    if (r.resolution === "Custom" && r.customPayload) {
      map[rowKey(r.tableCode, r.keyValue)] = r.customPayload;
    }
  }
  return map;
}

// Group flat field errors by row key -> column -> message for the grid and editor.
function groupErrors(errors: CustomFieldError[]): Record<string, Record<string, string>> {
  const map: Record<string, Record<string, string>> = {};
  for (const e of errors) {
    const k = rowKey(e.tableCode, e.keyValue);
    if (!map[k]) map[k] = {};
    map[k][e.column] = e.message;
  }
  return map;
}

interface GabSyncManagementProps {
  productGuid: string;
  productName: string;
  onClose?: () => void;
}

function GabSyncManagement({ productGuid, productName, onClose }: GabSyncManagementProps) {
  const authContext = useContext(AuthContext);
  const authState = authContext.authState();
  const token: string | undefined = authState?.accessToken?.accessToken;

  const [mode, setMode] = useState<Mode>("compare");
  const [step, setStep] = useState<Step>("select");
  const [comparing, setComparing] = useState<boolean>(false);
  // Shown inline on the start panel (not just as a dismissable toast) so a failed compare stays
  // visible until the operator acts on it.
  const [compareError, setCompareError] = useState<string | null>(null);
  const [compareDefaults, setCompareDefaults] = useState<GabCompareDefaults | null>(null);

  const [detail, setDetail] = useState<GabSyncRunDetail | null>(null);
  const [resolutions, setResolutions] = useState<ResolutionMap>({});
  const [links, setLinks] = useState<Record<string, string>>({});
  // Rows the user has explicitly acted on — drives the review-screen progress bar. Distinct from
  // `resolutions`, which is always fully seeded so submission has a sensible default per row.
  const [reviewedKeys, setReviewedKeys] = useState<Set<string>>(new Set());
  const [showUnchanged, setShowUnchanged] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [applying, setApplying] = useState<boolean>(false);
  const [applyResult, setApplyResult] = useState<GabSyncRunDetail | null>(null);
  const [applyError, setApplyError] = useState<string | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);

  const [customPayloads, setCustomPayloads] = useState<CustomPayloadMap>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, Record<string, string>>>({});

  const [historyRuns, setHistoryRuns] = useState<GabSyncRunSummary[]>([]);
  const [historyLoading, setHistoryLoading] = useState<boolean>(false);

  const loadHistory = useCallback(() => {
    if (!token) return;
    setHistoryLoading(true);
    fetchRunsForProduct(token, productGuid)
      .then((rows) => setHistoryRuns(rows))
      .catch((e) => setNotice({ style: "error", message: e.message }))
      .finally(() => setHistoryLoading(false));
  }, [token, productGuid]);

  useEffect(() => {
    if (mode === "history") loadHistory();
  }, [mode, loadHistory]);

  // Pre-load the environment's configured name-suffix defaults for the start screen. Fall back to a
  // blank set so the panel still works if the endpoint is unavailable.
  useEffect(() => {
    if (!token) return;
    fetchCompareDefaults(token)
      .then(setCompareDefaults)
      .catch(() =>
        setCompareDefaults({ sourceLabel: "Prod Backup", sourceNameSuffix: "", targetNameSuffix: "" })
      );
  }, [token]);

  const openDetail = useCallback((d: GabSyncRunDetail) => {
    setDetail(d);
    setResolutions(seedResolutions(d.session, d.resolutions));
    setLinks(parentLinks(d.session));
    setCustomPayloads(seedCustomPayloads(d.resolutions));
    setFieldErrors({});
    setShowUnchanged(false);
    // Rows carried over from a saved/resumed run count as already reviewed.
    setReviewedKeys(new Set((d.resolutions ?? []).map((r) => rowKey(r.tableCode, r.keyValue))));
    setStep("review");
  }, []);

  const handleCompare = useCallback(async (sourceNameSuffix: string, targetNameSuffix: string, newOnly: boolean) => {
    if (!token) return;
    setComparing(true);
    setNotice(null);
    setCompareError(null);
    try {
      const summary = await startCompare(token, productGuid, { sourceNameSuffix, targetNameSuffix, newOnly });
      const d = await fetchRun(token, summary.id);
      openDetail(d);
    } catch (e: any) {
      if (typeof e?.message === "string" && e.message.toLowerCase().includes("active")) {
        try {
          const runs = await fetchRunsForProduct(token, productGuid);
          const active = runs.find(
            (r) => r.status === "Draft" || r.status === "AwaitingReview"
          );
          if (active) {
            const d = await fetchRun(token, active.id);
            setNotice({ style: "info", message: "Resumed the existing in-progress session." });
            openDetail(d);
            return;
          }
        } catch {
          /* fall through */
        }
      }
      // `||`, not `??`: an empty-string message must still fall back to something readable.
      setCompareError(e?.message || "Compare failed.");
    } finally {
      setComparing(false);
    }
  }, [token, productGuid, openDetail]);

  // Mark a row (and its parent, so the cascade's auto-selection is reflected) as reviewed.
  const markReviewed = useCallback(
    (next: Set<string>, key: string) => {
      next.add(key);
      const parent = links[key];
      if (parent) next.add(parent);
    },
    [links]
  );

  const handleResolve = useCallback(
    (key: string, value: GabRowResolutionType) => {
      setResolutions((m) => applyResolution(m, links, key, value));
      setReviewedKeys((s) => {
        const next = new Set(s);
        markReviewed(next, key);
        return next;
      });
    },
    [links, markReviewed]
  );

  const handleBulk = useCallback(
    (tableCode: string, target: BulkTarget, value: GabRowResolutionType) => {
      if (!detail) return;
      setResolutions((m) => bulkResolve(m, links, detail.session, tableCode, target, value));
      setReviewedKeys((s) => {
        const next = new Set(s);
        const table = detail.session.tables.find((t) => t.tableCode === tableCode);
        if (table) {
          for (const row of table.rows) {
            if (!isResolvable(row)) continue;
            if (target === "new" && row.changeType !== "New") continue;
            if (target === "changed" && row.changeType !== "Changed") continue;
            markReviewed(next, rowKey(row.tableCode, row.keyValue));
          }
        }
        return next;
      });
    },
    [detail, links, markReviewed]
  );

  // Bulk "Clear": drop the shown rows back to their seeded default and un-review them.
  const handleClear = useCallback(
    (keys: string[]) => {
      if (!detail) return;
      const defaults = initialResolutions(detail.session);
      setResolutions((m) => {
        const next = { ...m };
        for (const k of keys) if (defaults[k] !== undefined) next[k] = defaults[k];
        return next;
      });
      setReviewedKeys((s) => {
        const next = new Set(s);
        for (const k of keys) next.delete(k);
        return next;
      });
      setCustomPayloads((cp) => {
        const next = { ...cp };
        for (const k of keys) delete next[k];
        return next;
      });
      setFieldErrors((fe) => {
        const next = { ...fe };
        for (const k of keys) delete next[k];
        return next;
      });
    },
    [detail]
  );

  const handleSaveCustom = useCallback(
    (key: string, _row: RowDiff, payload: Record<string, string | null>) => {
      setCustomPayloads((cp) => ({ ...cp, [key]: payload }));
      setResolutions((m) => applyResolution(m, links, key, "Custom"));
      setReviewedKeys((s) => {
        const next = new Set(s);
        markReviewed(next, key);
        return next;
      });
      setFieldErrors((fe) => {
        const next = { ...fe };
        delete next[key];
        return next;
      });
    },
    [links, markReviewed]
  );

  const handleSubmit = useCallback(async () => {
    if (!token || !detail) return;
    setSubmitting(true);
    try {
      const list = toResolutionList(detail.session, resolutions, customPayloads);
      const summary = await submitResolutions(token, detail.summary.id, list);
      setDetail({ ...detail, summary });

      // Validate any hand-edited rows against the target before allowing apply.
      if (list.some((r) => r.resolution === "Custom")) {
        const errors = await validateCustom(token, detail.summary.id);
        if (errors.length > 0) {
          setFieldErrors(groupErrors(errors));
          setNotice({
            style: "error",
            message: `Fix ${errors.length} invalid custom field(s) before applying.`,
          });
          setStep("review");
          return;
        }
      }

      setFieldErrors({});
      setNotice({ style: "success", message: "Resolutions saved. Confirm and apply." });
      setStep("apply");
    } catch (e: any) {
      setNotice({ style: "error", message: e?.message || "Failed to save resolutions." });
    } finally {
      setSubmitting(false);
    }
  }, [token, detail, resolutions, customPayloads]);

  const handleApply = useCallback(
    async (confirmation: string) => {
      if (!token || !detail) return;
      setApplying(true);
      setApplyError(null);
      try {
        const result = await applyRun(token, detail.summary.id, confirmation);
        setApplyResult(result);
        setNotice({ style: "success", message: `Applied to ${result.summary.targetLabel}.` });
        setStep("result");
      } catch (e: any) {
        const msg = e?.message || "Apply failed.";
        setApplyError(msg);
        setNotice({ style: "error", message: msg });
      } finally {
        setApplying(false);
      }
    },
    [token, detail]
  );

  const handleDiscard = useCallback(async () => {
    if (!token || !detail) {
      setStep("select");
      setDetail(null);
      setReviewedKeys(new Set());
      return;
    }
    try {
      await cancelRun(token, detail.summary.id);
      setNotice({ style: "info", message: "Session discarded." });
    } catch (e: any) {
      setNotice({ style: "error", message: e?.message || "Failed to discard session." });
    } finally {
      setDetail(null);
      setReviewedKeys(new Set());
      setStep("select");
    }
  }, [token, detail]);

  const finishRun = useCallback(() => {
    setDetail(null);
    setApplyResult(null);
    setApplyError(null);
    setCustomPayloads({});
    setFieldErrors({});
    setReviewedKeys(new Set());
    setStep("select");
  }, []);

  const applyCounts = detail
    ? summarize(detail.session, resolutions).reduce(
        (acc, t) => ({
          inserts: acc.inserts + t.insert,
          overwrites: acc.overwrites + t.overwrite,
          customs: acc.customs + t.custom,
        }),
        { inserts: 0, overwrites: 0, customs: 0 }
      )
    : { inserts: 0, overwrites: 0, customs: 0 };

  const notifications = (
    <div className={styles.noticeLayer}>
      <LNotificationGroup>
        <LFade>
          {notice && (
            <LNotification
              type={{ style: notice.style, icon: true }}
              closable
              onClose={() => setNotice(null)}
            >
              <span>{notice.message}</span>
            </LNotification>
          )}
        </LFade>
      </LNotificationGroup>
    </div>
  );

  // The review step takes over the whole surface with its own top bar (the redesign). Every other
  // step renders inside a simple full-screen shell with a header + the New Compare / History tabs.
  if (mode === "compare" && step === "review" && detail) {
    return (
      <div className={styles.gabSyncShell}>
        {notifications}
        <SyncReview
          session={detail.session}
          resolutions={resolutions}
          reviewedKeys={reviewedKeys}
          fieldErrors={fieldErrors}
          showUnchanged={showUnchanged}
          submitting={submitting}
          onToggleUnchanged={setShowUnchanged}
          onResolve={handleResolve}
          onBulk={handleBulk}
          onClear={handleClear}
          onSaveCustom={handleSaveCustom}
          onDiscard={handleDiscard}
          onRunSync={handleSubmit}
        />
      </div>
    );
  }

  return (
    <div className={styles.gabSyncShell}>
      {notifications}
      <div className={styles.stepShell}>
        <div className={styles.gabSyncHeader}>
          <div className={styles.gabSyncTitle}>
            <FontAwesomeIcon icon={faCodeCompare} /> GAB Sync · {productName}
          </div>
          {onClose && (
            <LButton look="flat" onClick={onClose}>
              <FontAwesomeIcon icon={faXmark} /> Close
            </LButton>
          )}
        </div>

        <div className={styles.modeTabs}>
          <button
            type="button"
            className={`${styles.modeTab} ${mode === "compare" ? styles.modeTabActive : ""}`}
            onClick={() => setMode("compare")}
          >
            New Compare
          </button>
          <button
            type="button"
            className={`${styles.modeTab} ${mode === "history" ? styles.modeTabActive : ""}`}
            onClick={() => setMode("history")}
          >
            History
          </button>
        </div>

        <div className={styles.stepBody}>
          {mode === "history" && (
            <RunHistory
              runs={historyRuns}
              loading={historyLoading}
              onRefresh={loadHistory}
              loadDetail={(id) => fetchRun(token ?? "", id)}
            />
          )}

          {mode === "compare" && step === "select" && (
            <SyncStartPanel
              productName={productName}
              busy={comparing}
              error={compareError}
              defaults={compareDefaults}
              onCompare={handleCompare}
            />
          )}

          {mode === "compare" && step === "apply" && detail && (
            <ApplyPanel
              targetLabel={detail.session.targetLabel}
              inserts={applyCounts.inserts}
              overwrites={applyCounts.overwrites}
              customs={applyCounts.customs}
              applying={applying}
              error={applyError}
              onApply={handleApply}
              onBack={() => {
                setApplyError(null);
                setStep("review");
              }}
            />
          )}

          {mode === "compare" && step === "result" && applyResult && (
            <div className={styles.resultContainer}>
              <div className={styles.resultHeader}>
                <FontAwesomeIcon icon={faCircleCheck} className={styles.resultIcon} />
                <h2>Applied to {applyResult.summary.targetLabel}</h2>
              </div>
              <div className={styles.summaryTotals}>
                <strong>{applyResult.summary.appliedInserted}</strong> inserted ·{" "}
                <strong>{applyResult.summary.appliedUpdated}</strong> updated ·{" "}
                <strong>{applyResult.summary.appliedSkipped}</strong> skipped
              </div>
              {applyResult.snapshotTables.length > 0 && (
                <div className={styles.snapshotList}>
                  <span className={styles.summaryLabel}>Rollback snapshots:</span>{" "}
                  {applyResult.snapshotTables.map((t) => (
                    <code key={t} className={styles.snapshotTag}>
                      {t}
                    </code>
                  ))}
                </div>
              )}
              <div className={styles.stepNav}>
                <span />
                <LButton themeColor="primary" onClick={finishRun}>
                  Done
                </LButton>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export { GabSyncManagement };
